import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import Navbar from "../components/Navigation";
import Footer from "../components/Footer";
import ChatSidebar from "../components/ChatSidebar";
import IncognitoChatWindow from "../components/IncognitoChatWindow";
import SnackbarAlert from "../components/SnackbarAlert";
import useSnackbar from "../hooks/useSnackbar";
import useChatSessions from "../hooks/useChatSessions";
import useIncognitoChat from "../hooks/useIncognitoChat";
import { MaskGlyph } from "../components/QuickChatGlyphs";
import QC from "../components/quickChatTheme";

const IncognitoChat = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const windowRef = useRef(null);

  const AuthToken = localStorage.getItem("authToken");
  const UserToken = localStorage.getItem("userToken");

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    handleCloseSnackbar,
  } = useSnackbar();

  const { chatSession, fetchChatSessions } = useChatSessions(
    AuthToken,
    UserToken,
    showSnackbar
  );

  const incognito = useIncognitoChat({ showSnackbar });

  const handleNewChat = () => {
    incognito.reset();
    navigate("/main-chat");
  };

  const handleSidebarChatSelection = (chatId) => {
    navigate(`/main-chat/c/${chatId}`);
  };

  const handleOpenSidebar = () => {
    setSidebarOpen(true);
    fetchChatSessions?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        height: "100vh",
        backgroundColor: QC.bgPage,
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {/* 4px warm-amber brand stripe — sits at the very top of the viewport. */}
      <Box
        data-testid="quickchat-brand-stripe"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: QC.stripe,
          zIndex: 1300,
          pointerEvents: "none",
        }}
      />

      <Navbar
        onNewChat={handleNewChat}
        onMenuClick={handleOpenSidebar}
        showMenuButton
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flex: 1,
          paddingTop: { xs: 12, md: 14 },
          paddingX: 2,
          paddingBottom: 4,
        }}
      >
        {/* Eyebrow pill */}
        <Box
          data-testid="quickchat-eyebrow"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "5px 12px",
            borderRadius: "999px",
            backgroundColor: "rgba(212,200,184,0.06)",
            border: `1px solid ${QC.border}`,
            color: QC.fgMid,
            marginBottom: "20px",
          }}
        >
          <MaskGlyph size={16} color={QC.fgMid} />
          <Typography
            component="span"
            sx={{
              fontFamily: "Raleway, sans-serif",
              fontSize: "11.5px",
              fontWeight: 700,
              color: QC.fgMid,
              letterSpacing: "1.2px",
            }}
          >
            QUICK CHAT
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#f5f5f5",
            fontFamily: "Raleway, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "1.6rem", md: "30px" },
            letterSpacing: "0.2px",
            textAlign: "center",
          }}
        >
          Ask now, forget later.
        </Typography>
        <Typography
          sx={{
            color: QC.fgSub,
            fontFamily: "Poppins, sans-serif",
            fontSize: "14.5px",
            lineHeight: 1.55,
            maxWidth: 720,
            textAlign: "center",
            marginTop: "14px",
            marginBottom: "28px",
          }}
        >
          A faster, lighter chat for one-off questions. ACE answers directly — skipping the RAG pipeline — so replies come back quicker. Nothing is saved to your history or stored on the server.
        </Typography>

        <IncognitoChatWindow
          ref={windowRef}
          messages={incognito.messages}
          attachedFile={incognito.attachedFile}
          onAttachFile={incognito.attachFile}
          onRemoveFile={incognito.removeFile}
          onSend={incognito.sendMessage}
          onStop={incognito.stopStreaming}
          isStreaming={incognito.isStreaming}
          isSending={incognito.isSending}
          streamingMessageId={incognito.streamingMessageId}
          expiryNotice={incognito.expiryNotice}
          onDismissExpiryNotice={incognito.dismissExpiryNotice}
        />
      </Box>

      <ChatSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onNewChat={handleNewChat}
        chatSessions={chatSession}
        activeChatId={null}
        onChatSelection={handleSidebarChatSelection}
        selectedChatForMenu={null}
        onMouseEnter={() => {}}
        onMenuOpen={() => {}}
        onStartQuickChat={() => {
          setSidebarOpen(false);
          incognito.reset();
        }}
        quickChatActive
      />

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />

      <Footer />
    </Box>
  );
};

export default IncognitoChat;
